import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useTheme } from '../contexts/ThemeContext'
import { db } from '../lib/db'
import { loadFriendsData } from '../lib/friendsData'
import { readActivityScope, writeActivityScope } from '../lib/activityScope'
import {
  SHARE_CATEGORY_META, TIER_OPTIONS, tierPreset, deriveTier, shareBooleans,
  friendPrivacyPayload,
} from '../lib/friendPrivacy'
import { supabase } from '../lib/supabase'
import { sha as buildSha, date as buildDate, buildNumber, appVersionLabel } from '../lib/buildInfo'
import FriendRowMenu from '../components/FriendRowMenu'

// Version scheme (sourced from package.json via buildInfo.version):
//   v1 = the original men's-outing app (the first thing that shipped).
//   v2 = the first overhaul into a full golf suite.
//   v3 = the recent major overhaul adding games, outings, and leagues.
// Current: 3.2. The human-readable "TournyFlex vX.Y" line below derives its
// number from buildInfo.version (major.minor only, patch dropped) via
// appVersionLabel() so a package.json bump flows to the UI automatically — do
// NOT hardcode the version string here again. Owner version checks expect the
// major.minor form (e.g. "v3.2"), never "v3.2.0".
const APP_VERSION_LABEL = appVersionLabel()
import {
  pushSupported, notificationPermission, enablePush, sendTestPush,
  getNotificationPreferences, updateNotificationPreferences,
  subscribeForegroundMessages, isPushConfigured, notificationFlagsPayload,
} from '../lib/push'

// Display labels only — the stored theme value is the object KEY
// (light/classic/bold), persisted to localStorage by ThemeContext. Renaming
// a `label` here does NOT change any saved theme's appearance; the key,
// preview swatch, and desc stay bound to the underlying CSS theme.
// (D-263: Light→Classic, Classic→Retro, Bold→Modern — display-only shuffle.)
const THEME_META = {
  light:   { label: 'Classic', desc: 'Warm paper, outdoor readability', preview: '#f4f1ea' },
  classic: { label: 'Retro',   desc: 'Heritage forest + brass', preview: '#1d3527' },
  bold:    { label: 'Modern',  desc: 'Crisp white + dark green', preview: '#ffffff' },
}

export default function MePage() {
  const { player, signOutAccount } = useAuth()
  const { theme, setGolfTheme, themes } = useTheme()
  const navigate = useNavigate()

  return (
    <div className="page">
      <div className="container">
        <div className="page-header">
          <p className="eyebrow">Account</p>
          <h1>Profile</h1>
        </div>

        {player && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{
                width: 52, height: 52, borderRadius: '50%',
                background: 'var(--gold)', color: 'var(--green-deep)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '1.3rem', fontWeight: 700, fontFamily: 'var(--font-display)',
              }}>
                {(player.first_name || player.name || '?')[0].toUpperCase()}
              </div>
              <div>
                <h3 style={{ marginBottom: 2 }}>{player.display_name || player.name}</h3>
                {player.email && <p className="text-muted text-xs">{player.email}</p>}
              </div>
            </div>

            <hr className="divider" />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {player.starter_handicap_index != null && (
                <div>
                  <p className="text-xs text-muted" style={{ marginBottom: 2 }}>Handicap Index</p>
                  <p style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, fontSize: '1.1rem' }}>
                    {player.starter_handicap_index}
                  </p>
                </div>
              )}
              {player.ghin_number && (
                <div>
                  <p className="text-xs text-muted" style={{ marginBottom: 2 }}>GHIN</p>
                  <p style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, fontSize: '1.1rem' }}>
                    {player.ghin_number}
                  </p>
                </div>
              )}
            </div>

            <button
              className="btn btn-ghost btn-full"
              onClick={() => navigate('/profile-edit')}
              style={{ marginTop: 12, fontSize: '0.85rem' }}
            >
              Edit Profile
            </button>
          </div>
        )}

        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <h3 style={{ margin: 0 }}>Theme</h3>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            {themes.map(t => {
              const m = THEME_META[t]
              const active = theme === t
              return (
                <button
                  key={t}
                  onClick={() => setGolfTheme(t)}
                  title={m.desc}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                    background: 'none', border: 'none', cursor: 'pointer', padding: 4,
                  }}
                >
                  <div style={{
                    width: 36, height: 36, borderRadius: 8,
                    background: m.preview,
                    border: active ? '3px solid var(--gold)' : '2px solid var(--green-mid)',
                    boxShadow: active ? '0 0 0 2px var(--gold)' : 'none',
                    transition: 'all 0.15s',
                  }} />
                  <span style={{
                    fontSize: '0.7rem', fontWeight: active ? 700 : 500,
                    color: active ? 'var(--gold)' : 'var(--gray-400)',
                  }}>{m.label}</span>
                </button>
              )
            })}
          </div>
        </div>

        {player && <ActivityFeedSection />}

        {player && <FriendPrivacySection />}

        {player && <NotificationsSection />}

        {player?.is_founding_tester && <CreditsSection playerId={player.id} />}

        {player && <FriendsSection playerId={player.id} />}

        <DiagnosticsSection />

        <LegalSection />

        <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {player && (
            <button className="btn btn-ghost btn-full" onClick={signOutAccount}>
              Sign Out
            </button>
          )}
          {player && <DeleteAccountControl />}
        </div>

        <p style={{ textAlign: 'center', fontSize: '0.6rem', color: 'var(--gray-700)', paddingTop: 24, fontFamily: 'var(--font-mono)' }}>
          TournyFlex {APP_VERSION_LABEL} — © 2026 TournyFlex LLC
        </p>

        {/* Build-version stamp — compiled into the JS bundle at build time (see
            src/lib/buildInfo.js). Because the bundle is service-worker-cached,
            this reflects the LOADED (cached) build on THIS device, not the
            server's latest. Support/testers read it off to spot a stale build.
            Own-app build info, not a promo — compliant with the ad policy. */}
        <p style={{ textAlign: 'center', fontSize: '0.7rem', color: 'var(--gray-700)', paddingBottom: 24, fontFamily: 'var(--font-mono)' }}>
          build {buildNumber} · {buildSha} · {buildDate}
        </p>
      </div>
    </div>
  )
}

// DeleteAccountControl — in-app account deletion (Apple 5.1.1(v)). A destructive
// "Delete account" button that opens a confirm modal, then calls the authed
// delete-account function (soft-delete: stamps players.deletion_requested_at)
// and signs the user out. Signing back in surfaces the restore prompt
// (RestoreAccountGate in App.jsx), so this is reversible until a purge.
function DeleteAccountControl() {
  const { signOutAccount } = useAuth()
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const doDelete = async () => {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) {
        setError('Please sign in again to delete your account.')
        setBusy(false)
        return
      }
      const res = await fetch('/.netlify/functions/delete-account', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
      })
      if (!res.ok) {
        const json = await res.json().catch(() => ({}))
        setError(json.error || 'Could not delete your account. Please try again.')
        setBusy(false)
        return
      }
      // Success — sign out. The pending deletion blocks re-entry until restore.
      await signOutAccount()
    } catch (e) {
      setError(e.message || 'Network error — please try again.')
      setBusy(false)
    }
  }

  return (
    <>
      <button
        className="btn btn-ghost btn-full"
        onClick={() => { setError(null); setConfirming(true) }}
        style={{ color: 'var(--danger, #c0392b)' }}
      >
        Delete account
      </button>

      {confirming && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 24,
        }}>
          <div className="card" style={{ maxWidth: 380, width: '100%' }}>
            <h3 style={{ marginTop: 0, marginBottom: 8 }}>Delete account</h3>
            <p className="text-muted text-sm" style={{ marginBottom: 20 }}>
              Deleting your account signs you out. You can restore it by signing
              in again.
            </p>
            {error && (
              <p className="text-sm" style={{ color: 'var(--danger, #c0392b)', marginBottom: 12 }}>
                {error}
              </p>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                className="btn btn-full"
                onClick={doDelete}
                disabled={busy}
                style={{ background: 'var(--danger, #c0392b)', color: '#fff', border: 'none' }}
              >
                {busy ? 'Deleting…' : 'Delete my account'}
              </button>
              <button
                className="btn btn-ghost btn-full"
                onClick={() => setConfirming(false)}
                disabled={busy}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// LegalSection — in-app links to legal documents (audit M4).
// Apple reviewers commonly ask for a Terms link in Settings; Play Data
// Safety expects an in-app link too. Terms and Privacy route internally
// (the SPA TermsPage / PrivacyPage hard-redirect to
// tournyflex.com/{terms,privacy}, so no target="_blank" is needed);
// Cookie Policy and Data Deletion have no in-app route and open the
// tournyflex.com pages in a new tab.
function LegalSection() {
  const linkStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 0',
    color: 'var(--cream)',
    textDecoration: 'none',
    fontSize: '0.9rem',
    fontWeight: 500,
  }
  const internalChevron = (
    <span aria-hidden="true" style={{ color: 'var(--gray-500)', fontSize: '1rem', lineHeight: 1 }}>›</span>
  )
  const externalChevron = (
    <span aria-hidden="true" style={{ color: 'var(--gray-500)', fontSize: '0.85rem', lineHeight: 1 }}>↗</span>
  )

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h3 style={{ margin: 0, marginBottom: 6 }}>Legal</h3>

      <a href="/terms" style={linkStyle}>
        <span>Terms of Service</span>
        {internalChevron}
      </a>
      <hr className="divider" style={{ margin: '2px 0' }} />

      <a href="/privacy" style={linkStyle}>
        <span>Privacy Policy</span>
        {internalChevron}
      </a>
      <hr className="divider" style={{ margin: '2px 0' }} />

      <a
        href="https://tournyflex.com/cookies"
        target="_blank"
        rel="noopener noreferrer"
        style={linkStyle}
      >
        <span>Cookie Policy</span>
        {externalChevron}
      </a>
      <hr className="divider" style={{ margin: '2px 0' }} />

      <a
        href="https://tournyflex.com/data-deletion"
        target="_blank"
        rel="noopener noreferrer"
        style={linkStyle}
      >
        <span>Data Deletion</span>
        {externalChevron}
      </a>
    </div>
  )
}

// 'My activity feed' scope preference (D-277). A per-device profile setting
// that sets the DEFAULT scope of the Home Recent Activity feed:
//   'all'        — Me + Friends (default)
//   'just_me'    — Me only
//   'my_friends' — Friends only
// Persisted to localStorage (key tf_activity_scope) via the activityScope
// helper — no DB column (D-269 client-side view filter). This is NOT the
// inline Home dropdown D-277 explicitly rules out; the All Rounds page keeps
// its own separate filter. HomePage re-reads this on its next visit.
const ACTIVITY_FEED_OPTIONS = [
  { value: 'all',        label: 'Me + Friends' },
  { value: 'just_me',    label: 'Me only' },
  { value: 'my_friends', label: 'Friends only' },
]

function ActivityFeedSection() {
  const [scope, setScope] = useState(readActivityScope)

  const choose = (v) => {
    const next = writeActivityScope(v)
    setScope(next)
  }

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h3 style={{ margin: 0, marginBottom: 4 }}>My activity feed</h3>
      <p className="text-muted text-xs" style={{ margin: '0 0 10px' }}>
        Choose whose rounds show in Recent Activity on your Home screen.
      </p>
      <div style={{ display: 'flex', gap: 8 }}>
        {ACTIVITY_FEED_OPTIONS.map((opt) => {
          const active = scope === opt.value
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => choose(opt.value)}
              aria-pressed={active}
              style={{
                flex: 1,
                padding: '8px 6px',
                borderRadius: 'var(--radius)',
                cursor: 'pointer',
                fontSize: '0.78rem',
                fontWeight: active ? 700 : 500,
                border: active ? '1px solid var(--gold)' : '1px solid var(--green-mid)',
                background: active ? 'rgba(212,185,122,0.12)' : 'var(--green-dark)',
                color: active ? 'var(--gold)' : 'var(--gray-400)',
                transition: 'all 0.15s',
              }}
            >
              {opt.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// Push notifications settings. Master toggle + 5 per-type toggles, persisted
// to notification_preferences via the notification-preferences function.
// Enabling requests OS permission + registers an FCM token (behind an explicit
// tap — NEVER auto-prompt on load). Foreground pushes surface as an in-app
// toast here (background pushes are shown by the FCM service worker). If the
// browser can't do web push, the section explains that instead of offering a
// dead toggle. If the build has no Firebase env vars, enable fails gracefully
// with a message rather than throwing.
const NOTIF_TYPES = [
  { key: 'round_invites', label: 'Round invites', desc: 'When someone invites you to a round or outing' },
  { key: 'score_updates', label: 'Score updates', desc: 'Corrections or updates to your scores' },
  { key: 'event_reminders', label: 'Event reminders', desc: 'Tee-time and event reminders' },
  { key: 'friend_requests', label: 'Friend requests', desc: 'New and accepted friend requests' },
  { key: 'league_standings', label: 'League standings', desc: 'Changes to your league standings' },
]

function ToggleSwitch({ on, disabled, onClick }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      disabled={disabled}
      onClick={onClick}
      style={{
        width: 44, height: 26, borderRadius: 13, border: 'none', flexShrink: 0,
        cursor: disabled ? 'not-allowed' : 'pointer',
        background: on ? 'var(--gold)' : 'var(--green-mid)',
        opacity: disabled ? 0.5 : 1,
        position: 'relative', transition: 'background 0.15s', padding: 0,
      }}
    >
      <span style={{
        position: 'absolute', top: 3, left: on ? 21 : 3,
        width: 20, height: 20, borderRadius: '50%', background: '#fff',
        transition: 'left 0.15s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
      }} />
    </button>
  )
}

function NotificationsSection() {
  const supported = pushSupported()
  const [permission, setPermission] = useState(() => (supported ? notificationPermission() : 'denied'))
  const [prefs, setPrefs] = useState(null)
  const [loaded, setLoaded] = useState(false)
  const [busy, setBusy] = useState(false)
  const [testing, setTesting] = useState(false)
  const [toast, setToast] = useState(null)

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3500) }

  // Load stored preferences.
  useEffect(() => {
    let active = true
    getNotificationPreferences()
      .then((data) => { if (active) setPrefs(data?.preferences || null) })
      .catch(() => { /* table not migrated yet — leave null, defaults shown */ })
      .finally(() => { if (active) setLoaded(true) })
    return () => { active = false }
  }, [])

  // Foreground push → in-app toast (background handled by the SW).
  // Only subscribe once push is actually usable: the browser supports it, FCM
  // is configured on this build, AND the user has granted permission. This
  // avoids loading the firebase SDK + running initializeApp() on every /me
  // visit (and the unhandled promise rejection initializeApp() throws when the
  // VITE_FIREBASE_* env vars aren't set yet).
  useEffect(() => {
    if (!supported || !isPushConfigured() || permission !== 'granted') return
    let unsub = () => {}
    let active = true
    subscribeForegroundMessages((payload) => {
      const n = payload?.notification || payload?.data || {}
      showToast(n.title ? `${n.title}${n.body ? ' — ' + n.body : ''}` : 'New notification')
    })
      .then((fn) => { if (active) unsub = fn || (() => {}); else if (fn) fn() })
      .catch(() => { /* firebase unavailable / not configured — no foreground toast */ })
    return () => { active = false; try { unsub() } catch {} }
  }, [supported, permission])

  const pushOn = permission === 'granted' && (prefs?.push_enabled ?? true)

  const persist = async (next) => {
    setPrefs(next)
    try {
      // Send ONLY the notification flags this card owns. The friend-privacy
      // card writes the same notification_preferences row (share_* +
      // friend_privacy); sending the whole merged object here would re-post a
      // stale snapshot of those columns and silently revert a privacy change
      // the user just made in the other card (F-039 two-writer review).
      const data = await updateNotificationPreferences(notificationFlagsPayload(next))
      if (data?.preferences) setPrefs(data.preferences)
    } catch (e) {
      showToast(e.message || 'Could not save preferences.')
    }
  }

  const toggleMaster = async () => {
    if (busy) return
    if (!pushOn) {
      // Turning on: ensure OS permission + a registered token first.
      setBusy(true)
      try {
        const res = await enablePush()
        if (!res.ok) {
          setPermission(notificationPermission())
          const msg = res.reason === 'permission_denied'
            ? 'Notifications are blocked in your browser settings.'
            : res.reason === 'unsupported'
              ? 'This browser can’t receive push notifications.'
              : res.reason === 'no_token'
                ? 'Could not register this device for push.'
                : 'Could not enable notifications.'
          showToast(msg)
          setBusy(false)
          return
        }
        setPermission('granted')
        await persist({ ...(prefs || {}), push_enabled: true })
        showToast('Notifications enabled.')
      } catch (e) {
        showToast(e.message || 'Could not enable notifications.')
      } finally {
        setBusy(false)
      }
    } else {
      // Turning off: keep the token, just suppress sends.
      await persist({ ...(prefs || {}), push_enabled: false })
    }
  }

  const toggleType = async (key) => {
    if (!pushOn) return
    const current = prefs?.[key] ?? true
    await persist({ ...(prefs || {}), [key]: !current })
  }

  const sendTest = async () => {
    if (testing) return
    setTesting(true)
    try {
      const res = await sendTestPush()
      if (res?.reason === 'no_tokens') showToast('No registered device yet — enable notifications first.')
      else if (res?.skipped) showToast('Test skipped — notifications are turned off.')
      else showToast('Test sent — you should get a push shortly.')
    } catch (e) {
      showToast(e.message || 'Could not send a test.')
    } finally {
      setTesting(false)
    }
  }

  if (!loaded) return null

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h3 style={{ margin: 0, marginBottom: 10 }}>Notifications</h3>

      {!supported ? (
        <p className="text-muted text-sm" style={{ margin: 0 }}>
          This browser can&rsquo;t receive push notifications. Install TournyFlex to your
          home screen (or use Chrome on Android) to turn them on.
        </p>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <strong style={{ fontSize: '0.9rem' }}>Push notifications</strong>
              <p className="text-muted text-xs" style={{ margin: '2px 0 0' }}>
                {permission === 'denied'
                  ? 'Blocked in your browser settings — allow notifications for this site to turn on.'
                  : 'Get alerts for invites, scores, events, and more.'}
              </p>
            </div>
            <ToggleSwitch on={pushOn} disabled={busy || permission === 'denied'} onClick={toggleMaster} />
          </div>

          {pushOn && (
            <>
              <hr className="divider" />
              {NOTIF_TYPES.map((t) => (
                <div key={t.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{t.label}</span>
                    <p className="text-muted text-xs" style={{ margin: '2px 0 0' }}>{t.desc}</p>
                  </div>
                  <ToggleSwitch on={prefs?.[t.key] ?? true} onClick={() => toggleType(t.key)} />
                </div>
              ))}
              <button
                className="btn btn-ghost btn-full"
                onClick={sendTest}
                disabled={testing}
                style={{ marginTop: 4, fontSize: '0.85rem' }}
              >
                {testing ? 'Sending…' : 'Send me a test notification'}
              </button>
            </>
          )}
        </>
      )}

      {toast && <div className="toast">{toast}</div>}
    </div>
  )
}

// Outbound friend-privacy — "What friends can see about me" (F-039 /
// D-269…D-275). Tier fast-path (Private / Shared / Full) + an Advanced
// disclosure with per-category toggles that flip the stored tier to 'custom'.
// Persists through the shared notification-preferences function
// (notification_preferences.share_* columns + the players.friend_privacy tier
// mirror). Name + avatar are ALWAYS shared with a friend and have no toggle.
// Until the owner-run migration lands, the server reports share_columns_ready:
// false and settings don't persist end-to-end — the card shows a note.
function FriendPrivacySection() {
  const [prefs, setPrefs] = useState(null)
  const [loaded, setLoaded] = useState(false)
  const [advanced, setAdvanced] = useState(false)
  const [migrationPending, setMigrationPending] = useState(false)
  const [toast, setToast] = useState(null)
  const showToast = (m) => { setToast(m); setTimeout(() => setToast(null), 3500) }

  useEffect(() => {
    let active = true
    getNotificationPreferences()
      .then((data) => {
        if (!active) return
        setPrefs(data?.preferences || null)
        if (data && data.share_columns_ready === false) setMigrationPending(true)
      })
      .catch(() => { /* table not migrated — leave null, defaults shown */ })
      .finally(() => { if (active) setLoaded(true) })
    return () => { active = false }
  }, [])

  const share = shareBooleans(prefs || {})
  const storedTier = prefs?.friend_privacy
  const tier = ['private', 'shared_only', 'full', 'custom'].includes(storedTier)
    ? storedTier
    : deriveTier(share)

  const persist = async (nextShare, nextTier) => {
    const next = { ...(prefs || {}), ...nextShare, friend_privacy: nextTier }
    setPrefs(next)
    try {
      // Send ONLY the fields this card owns (share_* + friend_privacy). The
      // sibling Notifications card writes the same row's notification flags;
      // posting the whole merged object here would revert a notification
      // toggle the user just made there (F-039 two-writer review).
      const data = await updateNotificationPreferences(friendPrivacyPayload(nextShare, nextTier))
      if (data?.preferences) setPrefs(data.preferences)
      if (data && data.share_columns_ready === false) {
        setMigrationPending(true)
        showToast('Saved — full sync turns on with the next app update.')
      }
    } catch (e) {
      showToast(e.message || 'Could not save your privacy settings.')
    }
  }

  const chooseTier = (t) => {
    if (t === 'private' || t === 'full') setAdvanced(false)
    persist(tierPreset(t), t)
  }

  const toggleCategory = (key) => {
    const nextShare = { ...share, [key]: !share[key] }
    // deriveTier lands back on a named tier if the toggles happen to match a
    // preset, otherwise 'custom'.
    persist(nextShare, deriveTier(nextShare))
  }

  if (!loaded) return null

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h3 style={{ margin: 0, marginBottom: 4 }}>What friends can see about you</h3>
      <p className="text-muted text-xs" style={{ margin: '0 0 10px' }}>
        Your name and photo are always visible to friends. Choose how much of the rest they see.
      </p>

      <div style={{ display: 'flex', gap: 8 }}>
        {TIER_OPTIONS.map((opt) => {
          const active = tier === opt.value
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => chooseTier(opt.value)}
              aria-pressed={active}
              style={{
                flex: 1, padding: '8px 6px', borderRadius: 'var(--radius)', cursor: 'pointer',
                fontSize: '0.78rem', fontWeight: active ? 700 : 500,
                border: active ? '1px solid var(--gold)' : '1px solid var(--green-mid)',
                background: active ? 'rgba(212,185,122,0.12)' : 'var(--green-dark)',
                color: active ? 'var(--gold)' : 'var(--gray-400)',
                transition: 'all 0.15s',
              }}
            >
              {opt.label}
            </button>
          )
        })}
      </div>

      <p className="text-muted text-xs" style={{ margin: '8px 0 0' }}>
        {tier === 'custom'
          ? 'Custom — you’ve set these individually below.'
          : (TIER_OPTIONS.find((o) => o.value === tier)?.blurb || '')}
      </p>

      <button
        type="button"
        className="btn btn-ghost btn-full"
        onClick={() => setAdvanced((v) => !v)}
        style={{ marginTop: 10, fontSize: '0.82rem' }}
      >
        {advanced ? 'Hide advanced' : 'Advanced — choose per category'}
      </button>

      {advanced && (
        <>
          <hr className="divider" />
          {SHARE_CATEGORY_META.map((c) => (
            <div key={c.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{c.label}</span>
                <p className="text-muted text-xs" style={{ margin: '2px 0 0' }}>{c.desc}</p>
              </div>
              <ToggleSwitch on={!!share[c.key]} onClick={() => toggleCategory(c.key)} />
            </div>
          ))}
        </>
      )}

      {migrationPending && (
        <p className="text-muted text-xs" style={{ margin: '10px 0 0', fontStyle: 'italic' }}>
          Friend-privacy sharing finishes rolling out with the next app update — your choice here is remembered but not yet applied to what friends see.
        </p>
      )}

      {toast && <div className="toast">{toast}</div>}
    </div>
  )
}

// Surfaces the two diagnostic localStorage entries the scorecard writes when
// something goes sideways:
//   - tf:lastSaveError — the most recent save-path JS error (PR #83)
//   - tf:lastTeamGarble — most recent team_number out-of-range write (PR #86)
// Mobile has no DevTools, so this is the only way to surface the captured
// data for diagnosis. Section is hidden when both are empty. Tap a value to
// copy the JSON to clipboard so it can be pasted anywhere (text to me, etc).
function DiagnosticsSection() {
  const [items, setItems] = useState(() => {
    const out = {}
    try { out.lastSaveError = localStorage.getItem('tf:lastSaveError') || null } catch {}
    try { out.lastTeamGarble = localStorage.getItem('tf:lastTeamGarble') || null } catch {}
    return out
  })
  const [copiedKey, setCopiedKey] = useState(null)
  if (!items.lastSaveError && !items.lastTeamGarble) return null

  const copy = async (key, val) => {
    try {
      await navigator.clipboard.writeText(val)
      setCopiedKey(key)
      setTimeout(() => setCopiedKey(null), 2000)
    } catch {}
  }
  const clear = (key) => {
    try { localStorage.removeItem(`tf:${key}`) } catch {}
    setItems(prev => ({ ...prev, [key === 'lastSaveError' ? 'lastSaveError' : 'lastTeamGarble']: null }))
  }

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h3 style={{ margin: 0, marginBottom: 8 }}>Diagnostics</h3>
      <p className="text-muted text-sm" style={{ marginBottom: 12 }}>
        Captured when a save errors or team data looks wrong. Tap to copy, then send to support.
      </p>
      {items.lastSaveError && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <strong style={{ fontSize: '0.85rem' }}>Last save error</strong>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-ghost" style={{ fontSize: '0.7rem', padding: '4px 8px' }} onClick={() => copy('lastSaveError', items.lastSaveError)}>
                {copiedKey === 'lastSaveError' ? 'Copied' : 'Copy'}
              </button>
              <button className="btn btn-ghost" style={{ fontSize: '0.7rem', padding: '4px 8px' }} onClick={() => clear('lastSaveError')}>Clear</button>
            </div>
          </div>
          <pre style={{ fontSize: '0.65rem', fontFamily: 'var(--font-mono)', whiteSpace: 'pre-wrap', wordBreak: 'break-all', background: 'rgba(0,0,0,0.04)', padding: 8, borderRadius: 6, margin: 0, maxHeight: 200, overflow: 'auto' }}>
            {items.lastSaveError}
          </pre>
        </div>
      )}
      {items.lastTeamGarble && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <strong style={{ fontSize: '0.85rem' }}>Last team-number garble</strong>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-ghost" style={{ fontSize: '0.7rem', padding: '4px 8px' }} onClick={() => copy('lastTeamGarble', items.lastTeamGarble)}>
                {copiedKey === 'lastTeamGarble' ? 'Copied' : 'Copy'}
              </button>
              <button className="btn btn-ghost" style={{ fontSize: '0.7rem', padding: '4px 8px' }} onClick={() => clear('lastTeamGarble')}>Clear</button>
            </div>
          </div>
          <pre style={{ fontSize: '0.65rem', fontFamily: 'var(--font-mono)', whiteSpace: 'pre-wrap', wordBreak: 'break-all', background: 'rgba(0,0,0,0.04)', padding: 8, borderRadius: 6, margin: 0, maxHeight: 200, overflow: 'auto' }}>
            {items.lastTeamGarble}
          </pre>
        </div>
      )}
    </div>
  )
}

// Founding-tester credits. Balance display + Redeem action + recent history.
// Sim rounds don't earn (locked 2026-07-03); real feedback rounds do. See
// docs/founding-tester-credits.md.
//
// Redeem calls /.netlify/functions/redeem-credit — the atomic ledger-write
// + pro_current_period_end extension. It refuses when a live paid
// subscription is still auto-renewing (would waste the credit alongside
// the renewal); we surface that as an actionable message.
const CREDIT_CAP = 12

function fmtCreditDate(d) {
  if (!d) return null
  return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

function historyLabel(entry) {
  switch (entry.entry_type) {
    case 'earn':   return `Earned +${entry.amount}`
    case 'redeem': return `Redeemed ${entry.amount}`
    case 'expire': return `Expired ${entry.amount}`
    case 'adjust': return `${entry.amount > 0 ? 'Adjusted +' : 'Adjusted '}${entry.amount}`
    default:       return `${entry.entry_type} ${entry.amount}`
  }
}

function CreditsSection({ playerId }) {
  const [balance, setBalance] = useState(0)
  const [nextExpiry, setNextExpiry] = useState(null)
  const [history, setHistory] = useState([])
  const [loaded, setLoaded] = useState(false)
  const [redeeming, setRedeeming] = useState(false)
  const [toast, setToast] = useState(null)

  const refresh = async () => {
    try {
      const [balRes, histRes] = await Promise.all([
        db('get_credit_balance', { player_id: playerId }),
        db('get_credit_history', { player_id: playerId }),
      ])
      if (balRes?.data) {
        setBalance(balRes.data.balance || 0)
        setNextExpiry(balRes.data.next_expiry || null)
      }
      if (Array.isArray(histRes?.data)) {
        setHistory(histRes.data.slice(0, 5))
      }
    } catch {
      // Ledger table not migrated yet — leave defaults.
    }
    setLoaded(true)
  }

  useEffect(() => {
    if (!playerId) return
    let active = true
    refresh().finally(() => { if (!active) setLoaded(true) })
    return () => { active = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerId])

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3500) }

  const redeem = async () => {
    if (redeeming || balance < 1) return
    setRedeeming(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) {
        showToast('Please sign in again to redeem.')
        setRedeeming(false)
        return
      }
      const res = await fetch('/.netlify/functions/redeem-credit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ amount: 1 }),
      })
      const json = await res.json()
      if (!res.ok) {
        if (json.code === 'active_stripe_subscription') {
          showToast('Cancel auto-renewal on Pro before applying a credit.')
        } else if (json.code === 'insufficient_balance') {
          showToast('Not enough credits to redeem.')
        } else {
          showToast(json.error || 'Redeem failed. Please try again.')
        }
        setRedeeming(false)
        return
      }
      showToast(`+1 Pro month applied. Access now through ${fmtCreditDate(json.pro_current_period_end)}.`)
      await refresh()
    } catch (e) {
      showToast(e.message || 'Network error — try again.')
    } finally {
      setRedeeming(false)
    }
  }

  if (!loaded) return null

  return (
    <div id="credits" className="card" style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <h3 style={{ margin: 0 }}>Founding-Tester Credits</h3>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: '1.25rem', fontWeight: 700, color: 'var(--gold)' }}>
          {balance}<span style={{ color: 'var(--gray-500)', fontSize: '0.9rem', fontWeight: 400 }}>/{CREDIT_CAP}</span>
        </span>
      </div>

      <p className="text-muted text-sm" style={{ marginBottom: 6 }}>
        Earn 1 free Pro month per <strong>real</strong> round of feedback during the 2026
        founding season, up to {CREDIT_CAP}. Simulator rounds don't earn credits.
        Redeemable against individual Pro — not outing, event, or league fees.
      </p>

      {nextExpiry && (
        <p className="text-xs text-muted" style={{ marginBottom: 6 }}>
          Next credits expire {fmtCreditDate(nextExpiry)}.
        </p>
      )}

      <p className="text-xs" style={{ color: 'var(--gray-500)', marginBottom: 10 }}>
        All credits expire Dec 31, 2027.
      </p>

      <div style={{ display: 'grid', gap: 8 }}>
        <button
          className="btn btn-primary btn-full"
          disabled={redeeming || balance < 1}
          onClick={redeem}
        >
          {redeeming ? 'Applying…' : balance < 1 ? 'No credits yet' : 'Redeem 1 credit (1 Pro month)'}
        </button>
      </div>

      {history.length > 0 && (
        <>
          <hr className="divider" />
          <p className="text-xs text-muted" style={{ marginBottom: 6 }}>Recent activity</p>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {history.map(entry => (
              <li
                key={entry.id}
                style={{
                  display: 'flex', justifyContent: 'space-between',
                  fontSize: '0.8rem', padding: '4px 0',
                  borderBottom: '1px solid var(--gray-200, #eee)',
                }}
              >
                <span>{historyLabel(entry)}</span>
                <span className="text-muted">{fmtCreditDate(entry.created_at)}</span>
              </li>
            ))}
          </ul>
        </>
      )}

      {toast && (
        <div
          style={{
            position: 'fixed', bottom: 'calc(84px + env(safe-area-inset-bottom))', left: '50%', transform: 'translateX(-50%)',
            background: 'var(--green-deep)', color: 'white',
            padding: '10px 16px', borderRadius: 8, boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
            zIndex: 900, maxWidth: '90%',
          }}
        >
          {toast}
        </div>
      )}
    </div>
  )
}

function FriendsSection({ playerId }) {
  const [friends, setFriends] = useState([])
  const [pending, setPending] = useState([])
  const [suggestions, setSuggestions] = useState([])
  const [sent, setSent] = useState([]) // OUTGOING pending requests (durable, DB-backed)
  const [toast, setToast] = useState(null)
  const [loading, setLoading] = useState(true)
  // Search state — mirrors the pattern used by PlayerSearchInput on the
  // round-add flow so users get the same result semantics on the profile.
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [sentIds, setSentIds] = useState(new Set())

  const refresh = async () => {
    // Resilient load: each of the three queries is handled independently
    // (Promise.allSettled inside loadFriendsData) so a single failing
    // sibling — most plausibly suggest_friends timing out on a
    // heavy-history account — can NEVER blank the user's real accepted
    // friends. See src/lib/friendsData.js and F-040.
    try {
      const res = await loadFriendsData(playerId)
      // Apply each slice only when its own query succeeded, so a transient
      // failure keeps the last good list rather than blanking it.
      if (!res.friendsFailed) setFriends(res.friends)
      if (!res.pendingFailed) setPending(res.pending)
      if (!res.sentFailed) setSent(res.sent)
      if (!res.suggestionsFailed) {
        setSuggestions(res.suggestions)
      } else {
        // Non-blocking: suggestions couldn't load; friends/pending stand.
        console.warn('suggest_friends failed to load; showing friends without suggestions')
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { refresh() }, [playerId])

  // Debounced player search. Same 300ms debounce + 2-char minimum as
  // PlayerSearchInput so behavior stays consistent between round-add and
  // profile flows.
  useEffect(() => {
    if (!playerId) return
    const q = searchQuery.trim()
    if (q.length < 2) {
      setSearchResults([])
      setSearching(false)
      return
    }
    setSearching(true)
    const t = setTimeout(async () => {
      try {
        const { data } = await db('search_players', { query: q, player_id: playerId })
        setSearchResults(Array.isArray(data) ? data : [])
      } catch { setSearchResults([]) }
      finally { setSearching(false) }
    }, 300)
    return () => clearTimeout(t)
  }, [searchQuery, playerId])

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3000) }

  const acceptRequest = async (friendshipId) => {
    await db('respond_friend_request', { friendship_id: friendshipId, player_id: playerId, accept: true })
    showToast('Friend added!')
    refresh()
  }

  const declineRequest = async (friendshipId) => {
    await db('respond_friend_request', { friendship_id: friendshipId, player_id: playerId, accept: false })
    showToast('Request declined')
    refresh()
  }

  const sendRequest = async (toPlayerId) => {
    const result = await db('send_friend_request', { from_player_id: playerId, to_player_id: toPlayerId })
    if (result.blocked) {
      showToast('Cannot send — this person is blocked')
    } else if (result.already_exists) {
      showToast('Request already sent')
    } else {
      showToast('Friend request sent!')
    }
    setSentIds(prev => new Set(prev).add(toPlayerId))
    refresh()
  }

  // ── Guideline 1.2 UGC safety: block + report ──────────────────────────
  const [reportTarget, setReportTarget] = useState(null) // { id, name } | null
  const [reportReason, setReportReason] = useState('')
  const [reportDetails, setReportDetails] = useState('')
  const [reportBusy, setReportBusy] = useState(false)

  const blockUser = async (targetId, name) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Block ${name || 'this person'}? They won't be able to send you a friend request or see your activity, and they'll be removed from your friends and suggestions.`)) return
    try {
      // block_user handled errors ({ error: 'unauthorized' | 'invalid target' })
      // come back at HTTP 200, so db() does NOT throw on them — inspect the
      // returned shape and only confirm when a block was actually recorded.
      // Otherwise a safety action that silently failed server-side would still
      // show "Blocked" and hide the person (who could still contact the user).
      const result = await db('block_user', { blocked_player_id: targetId })
      if (result?.error || !result?.data?.blocked) {
        showToast('Could not block — try again')
        return
      }
      // Optimistically drop them from any on-screen list; refresh reconciles.
      setSearchResults(prev => prev.filter(x => x.id !== targetId))
      setSuggestions(prev => prev.filter(x => x.id !== targetId))
      setFriends(prev => prev.filter(x => x.friend_id !== targetId))
      showToast('Blocked')
      refresh()
    } catch {
      showToast('Could not block — try again')
    }
  }

  // ── Plain un-friend (distinct from Block) ─────────────────────────────
  // Removes the accepted friendship only — the person leaves the friends list
  // but is NOT blocked, so either party can re-add later via a normal request.
  const removeFriend = async (targetId, name) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove ${name || 'this person'} from your friends?`)) return
    try {
      // Handled errors ({ error }) return at HTTP 200, and a no-op DELETE comes
      // back { removed: false } — so inspect the shape and only confirm when a
      // friendship was actually deleted.
      const result = await db('remove_friend', { friend_id: targetId })
      if (result?.error || !result?.data?.removed) {
        showToast('Could not remove — try again')
        return
      }
      // Optimistically drop from the friends list; refresh reconciles.
      setFriends(prev => prev.filter(x => x.friend_id !== targetId))
      showToast('Removed from friends')
      refresh()
    } catch {
      showToast('Could not remove — try again')
    }
  }

  const submitReport = async () => {
    if (!reportTarget || !reportReason) return
    setReportBusy(true)
    try {
      const headers = { 'Content-Type': 'application/json' }
      try {
        const { data } = await supabase.auth.getSession()
        const token = data?.session?.access_token
        if (token) headers.Authorization = `Bearer ${token}`
      } catch { /* anonymous report still records */ }
      const res = await fetch('/.netlify/functions/report-content', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          reported_player_id: reportTarget.id,
          content_type: 'player',
          reason: reportReason,
          details: reportDetails.trim() || null,
        }),
      })
      if (!res.ok) throw new Error('report failed')
      showToast('Report submitted — thank you')
    } catch {
      showToast('Could not submit report — try again')
    } finally {
      setReportBusy(false)
      setReportTarget(null)
      setReportReason('')
      setReportDetails('')
    }
  }

  if (loading) return null

  // Compact, low-emphasis affordance shared by Report/Block buttons — small and
  // muted so it never competes with the primary Add/Accept actions.
  const safetyBtnStyle = {
    padding: '3px 8px', background: 'none', color: 'var(--gray-500)',
    border: '1px solid var(--green-mid)', borderRadius: 'var(--radius)',
    cursor: 'pointer', fontSize: '0.68rem', fontWeight: 500, flexShrink: 0,
  }

  const REPORT_REASONS = [
    'Abusive or offensive behavior',
    'Harassment or bullying',
    'Inappropriate name or content',
    'Spam or impersonation',
    'Other',
  ]

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h3 style={{ marginBottom: 14 }}>Friends</h3>

      {/* Search — same debounce + 2-char minimum as the round-add flow. */}
      <div style={{ marginBottom: pending.length > 0 || friends.length > 0 || suggestions.length > 0 ? 16 : 0 }}>
        <input
          className="input"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search for someone to add..."
          autoComplete="off"
        />
        {searching && searchQuery.trim().length >= 2 && (
          <p className="text-xs text-muted" style={{ marginTop: 4 }}>Searching...</p>
        )}
        {!searching && searchQuery.trim().length >= 2 && searchResults.length === 0 && (
          <p className="text-xs text-muted" style={{ marginTop: 4 }}>No matches</p>
        )}
        {searchResults.length > 0 && (
          <div style={{ marginTop: 8, borderRadius: 'var(--radius)', border: '1px solid var(--green-mid)', overflow: 'hidden' }}>
            {searchResults.map(p => {
              const alreadyFriend = !!p.is_friend
              // Durable pending flag (is_request_pending) survives reload;
              // sentIds gives instant optimistic feedback before refresh()
              // reloads from the DB. Either one shows "Requested".
              const requestPending = !!p.is_request_pending || sentIds.has(p.id)
              return (
                <div
                  key={p.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 10px', background: 'var(--green-dark)',
                    borderBottom: '1px solid var(--green-mid)',
                  }}
                >
                  <div style={{
                    width: 30, height: 30, borderRadius: '50%',
                    background: 'var(--green-mid)', color: 'var(--cream)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.78rem', fontWeight: 700, flexShrink: 0,
                  }}>
                    {(p.name || '?')[0].toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{p.name}</span>
                    {!p.auth_user_id && (
                      <span style={{ marginLeft: 6, fontSize: '0.68rem', color: 'var(--gray-500)' }}>· no account</span>
                    )}
                  </div>
                  <button
                    onClick={() => { setReportTarget({ id: p.id, name: p.name }); setReportReason(''); setReportDetails('') }}
                    style={safetyBtnStyle}
                  >
                    Report
                  </button>
                  {alreadyFriend ? (
                    <span style={{ fontSize: '0.72rem', color: 'var(--gold)', fontWeight: 600 }}>Friends</span>
                  ) : requestPending ? (
                    <span style={{ fontSize: '0.72rem', color: 'var(--gray-500)' }}>Requested</span>
                  ) : (
                    <button
                      onClick={() => sendRequest(p.id)}
                      style={{
                        padding: '4px 12px', background: 'none', color: 'var(--gold)',
                        border: '1px solid var(--gold)', borderRadius: 'var(--radius)',
                        cursor: 'pointer', fontSize: '0.75rem', fontWeight: 500,
                      }}
                    >
                      Add
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Pending requests */}
      {pending.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <p style={{ fontSize: '0.75rem', color: 'var(--gold)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
            Pending Requests ({pending.length})
          </p>
          {pending.map(req => (
            <div key={req.friendship_id} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 10px', marginBottom: 6,
              background: 'rgba(212,185,122,0.08)', border: '1px solid var(--gold)',
              borderRadius: 'var(--radius)',
            }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%',
                background: 'var(--green-mid)', color: 'var(--cream)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.8rem', fontWeight: 700, flexShrink: 0,
              }}>
                {(req.from_player_name || '?')[0].toUpperCase()}
              </div>
              <span style={{ flex: 1, fontSize: '0.85rem', fontWeight: 500 }}>{req.from_player_name}</span>
              <button
                onClick={() => acceptRequest(req.friendship_id)}
                style={{
                  padding: '4px 12px', background: 'var(--gold)', color: 'var(--green-deep)',
                  border: 'none', borderRadius: 'var(--radius)', cursor: 'pointer',
                  fontSize: '0.75rem', fontWeight: 600,
                }}
              >
                Accept
              </button>
              <button
                onClick={() => declineRequest(req.friendship_id)}
                style={{
                  padding: '4px 8px', background: 'none', color: 'var(--gray-500)',
                  border: '1px solid var(--green-mid)', borderRadius: 'var(--radius)',
                  cursor: 'pointer', fontSize: '0.75rem',
                }}
              >
                Decline
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Outgoing pending — people you've requested. DB-backed (get_sent_
          friend_requests) so it survives an app reopen, unlike the old
          in-memory sentIds. */}
      {sent.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <p style={{ fontSize: '0.75rem', color: 'var(--gray-400)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
            Requested ({sent.length})
          </p>
          {sent.map(req => (
            <div key={req.friendship_id} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 10px', marginBottom: 4,
              background: 'var(--green-dark)', borderRadius: 'var(--radius)',
              border: '1px solid var(--green-mid)',
            }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%',
                background: 'var(--green-mid)', color: 'var(--cream)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.8rem', fontWeight: 700, flexShrink: 0,
              }}>
                {(req.to_player_name || '?')[0].toUpperCase()}
              </div>
              <span style={{ flex: 1, fontSize: '0.85rem', fontWeight: 500 }}>{req.to_player_name}</span>
              <span style={{ fontSize: '0.72rem', color: 'var(--gray-500)' }}>Pending</span>
            </div>
          ))}
        </div>
      )}

      {/* Friends list */}
      {friends.length > 0 ? (
        <div style={{ marginBottom: suggestions.length > 0 ? 16 : 0 }}>
          {friends.map(f => (
            <div key={f.friendship_id} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 10px', marginBottom: 4,
              background: 'var(--green-dark)', borderRadius: 'var(--radius)',
              border: '1px solid var(--green-mid)',
            }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%',
                background: 'var(--green-mid)', color: 'var(--cream)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.8rem', fontWeight: 700, flexShrink: 0,
              }}>
                {(f.friend_name || '?')[0].toUpperCase()}
              </div>
              <span style={{ flex: 1, minWidth: 0, fontSize: '0.85rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.friend_name}</span>
              <FriendRowMenu
                label={f.friend_name}
                actions={[
                  { label: 'Report', onSelect: () => { setReportTarget({ id: f.friend_id, name: f.friend_name }); setReportReason(''); setReportDetails('') } },
                  { label: 'Block', onSelect: () => blockUser(f.friend_id, f.friend_name) },
                  { label: 'Remove friend', danger: true, onSelect: () => removeFriend(f.friend_id, f.friend_name) },
                ]}
              />
            </div>
          ))}
        </div>
      ) : (
        <p className="text-muted text-sm" style={{ marginBottom: suggestions.length > 0 ? 16 : 0 }}>
          No friends yet. Add players you've played with!
        </p>
      )}

      {/* Suggestions from shared rounds */}
      {suggestions.length > 0 && (
        <div>
          <p style={{ fontSize: '0.75rem', color: 'var(--gray-400)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
            People you've played with
          </p>
          {suggestions.map(s => (
            <div key={s.id} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 10px', marginBottom: 4,
              background: 'var(--green-dark)', borderRadius: 'var(--radius)',
              border: '1px solid var(--green-mid)',
            }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%',
                background: 'var(--green-mid)', color: 'var(--cream)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.8rem', fontWeight: 700, flexShrink: 0,
              }}>
                {(s.name || '?')[0].toUpperCase()}
              </div>
              <span style={{ flex: 1, fontSize: '0.85rem', fontWeight: 500 }}>{s.name}</span>
              <button
                onClick={() => sendRequest(s.id)}
                style={{
                  padding: '4px 12px', background: 'none', color: 'var(--gold)',
                  border: '1px solid var(--gold)', borderRadius: 'var(--radius)',
                  cursor: 'pointer', fontSize: '0.75rem', fontWeight: 500,
                }}
              >
                Add
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Guideline 1.2 zero-tolerance clause, surfaced at the UGC entry point
          (TermsPage is a hard redirect to tournyflex.com/terms and cannot be
          edited in-app, so the clause lives next to where users add/report
          people). */}
      <p className="text-xs text-muted" style={{ marginTop: 14, lineHeight: 1.5 }}>
        TournyFlex has zero tolerance for abusive, harassing, or objectionable
        content and behavior. Use Report or Block on anyone who violates this;
        offenders may have their content removed and their account suspended.
      </p>

      {/* Report modal — short reason picker + optional detail. */}
      {reportTarget && (
        <div
          onClick={() => !reportBusy && setReportTarget(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(0,0,0,0.55)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', padding: 16,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            className="card"
            style={{ maxWidth: 400, width: '100%', margin: 0 }}
          >
            <h3 style={{ marginBottom: 6 }}>Report {reportTarget.name || 'player'}</h3>
            <p className="text-xs text-muted" style={{ marginBottom: 12 }}>
              Tell us what's wrong. Reports go to the TournyFlex team for review.
            </p>
            <div style={{ marginBottom: 12 }}>
              {REPORT_REASONS.map(r => (
                <label key={r} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="report-reason"
                    checked={reportReason === r}
                    onChange={() => setReportReason(r)}
                  />
                  <span style={{ fontSize: '0.85rem' }}>{r}</span>
                </label>
              ))}
            </div>
            <textarea
              className="input"
              value={reportDetails}
              onChange={e => setReportDetails(e.target.value)}
              placeholder="Add any details (optional)"
              rows={3}
              style={{ marginBottom: 12, resize: 'vertical' }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setReportTarget(null)}
                disabled={reportBusy}
                style={{
                  padding: '6px 14px', background: 'none', color: 'var(--gray-500)',
                  border: '1px solid var(--green-mid)', borderRadius: 'var(--radius)',
                  cursor: 'pointer', fontSize: '0.8rem',
                }}
              >
                Cancel
              </button>
              <button
                onClick={submitReport}
                disabled={reportBusy || !reportReason}
                style={{
                  padding: '6px 14px',
                  background: reportReason ? 'var(--gold)' : 'var(--green-mid)',
                  color: reportReason ? 'var(--green-deep)' : 'var(--gray-500)',
                  border: 'none', borderRadius: 'var(--radius)',
                  cursor: reportReason && !reportBusy ? 'pointer' : 'default',
                  fontSize: '0.8rem', fontWeight: 600,
                }}
              >
                {reportBusy ? 'Submitting…' : 'Submit report'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className="toast success" style={{ marginTop: 8 }}>{toast}</div>}
    </div>
  )
}
